import { DateTime } from 'luxon'
import type { ReactElement, ReactNode } from 'react'

import SeriesQ from '@/queries/SeriesQ'
import UserQ from '@/queries/UserQ'
import WorldQ from '@/queries/WorldQ'
import { useAppContext } from '@/client/context/app'
import { useUser } from '@/client/context/user'
import { PageNotFoundError } from '@/client/error'
import type { Query } from '@/client/query'

import Layout from '@/components/Layout/Layout'
import PlatformHeader from '@/components/Layout/PlatformHeader'
import SeriesModal from '@/components/Series/SeriesLayout/SeriesModal'
import type { BreadcrumbDetails } from '@/components/Worlds/UI/types'

import {
  ERROR_SERIES_DELETED,
  ERROR_SERIES_NONEXISTENT_OR_LOGGED_OUT,
  ERROR_SERIES_NONEXISTENT_OR_NO_ACCESS,
} from './constants'
import SeriesNavbar from './SeriesNavbar/SeriesNavbar'
import type { SeriesTab } from './types'

type SeriesLayoutWorldDetails = {
  title: string
  slug: string
  iconUrl: string
  primaryColor: string
}

type SeriesLayoutSeriesDetails = {
  title: string
  id: number
  startDate: DateTime
  world: SeriesLayoutWorldDetails
  tutorDisplayName: string
}

export default function SeriesLayout({
  series,
  currentTab = null,
  title,
  description,
  headerOptions,
  children,
  hideNavbar = false,
}: {
  series: Query
  currentTab?: SeriesTab | null
  title: (seriesTitle: string) => string
  description?: ((series: SeriesLayoutSeriesDetails) => string) | null
  headerOptions: {
    heading: (seriesTitle: string) => string
    breadcrumbs?: (series: SeriesLayoutSeriesDetails) => BreadcrumbDetails[]
  }
  children: ReactNode
  hideNavbar?: boolean
}): ReactElement {
  const renderContext = useAppContext()
  const user = useUser()

  const seriesExists = series(SeriesQ.id) != null
  const seriesIsDeleted = series(SeriesQ.deleted_at) != null
  if (!renderContext.dryRun) {
    if (!seriesExists) {
      if (user.isLoggedOut) {
        throw new PageNotFoundError(ERROR_SERIES_NONEXISTENT_OR_LOGGED_OUT)
      }
      throw new PageNotFoundError(ERROR_SERIES_NONEXISTENT_OR_NO_ACCESS)
    }
    if (seriesIsDeleted) {
      throw new PageNotFoundError(ERROR_SERIES_DELETED)
    }
  }

  const world = series.one(SeriesQ.getWorld())
  const tutor = series.one(SeriesQ.getUser())
  const worldDetails = {
    title: world(WorldQ.title) ?? '',
    slug: world(WorldQ.slug) ?? '',
    iconUrl: world(WorldQ.icon_url) ?? '',
    primaryColor: world(WorldQ.primary_color) ?? '',
  }
  const seriesDetails: SeriesLayoutSeriesDetails = {
    id: series(SeriesQ.id) ?? 0,
    title: series(SeriesQ.title) ?? '',
    startDate: DateTime.fromISO(`${series(SeriesQ.getComputedStartDate())}`),
    world: worldDetails,
    tutorDisplayName: tutor(UserQ.display_name) ?? '',
  }
  const layoutDescription = description?.(seriesDetails)
  const layoutBreadcrumbs = headerOptions.breadcrumbs?.(seriesDetails)

  return (
    <Layout
      title={title(seriesDetails.title)}
      header={
        <PlatformHeader
          // world={worldDetails} TODO: Add this back in a subject way?
          heading={{
            title: headerOptions.heading(seriesDetails.title),
            breadcrumbs: layoutBreadcrumbs,
          }}
        />
      }
      meta={{
        title: seriesDetails?.title,
        description: layoutDescription,
      }}
      hideFooter
    >
      {!hideNavbar && currentTab != null ? (
        <SeriesNavbar currentTab={currentTab} series={series} />
      ) : null}
      <SeriesModal />
      {children}
    </Layout>
  )
}
